import { SectionCard } from '@atlas/ui-components'
import {
	APPLICABLE_RULES_TITLE,
	ApplicableRulesBodySkeleton,
	AssetListBodySkeleton,
	CAMPAIGN_LIST_TITLE,
	CampaignListBodySkeleton,
	DELIVERABLES_TITLE,
	DeliverablesBodySkeleton,
	TEXT_LIST_TITLE,
	TextListBodySkeleton,
} from '@atlas/ui-domain'
import { Skeleton } from '@atlas/ui-primitives'
import { Eyebrow } from '@/components/editorial/PageHeader'
import { ProductFilterRailSkeleton } from './ProductFilterRail'
import { PRODUCT_NAMING_TITLE, ProductNamingBodySkeleton } from './ProductNamingCard'
import { BRAND_KIT_TITLE, PRODUCT_PAGE_EYEBROW, ProductPageLayout } from './ProductPageLayout'
import { ResolvingForBarSkeleton } from './ResolvingForBar'

/** The breadcrumb's segments — a geography path, the brand, then the product. */
function BreadcrumbSkeleton() {
	return (
		<div className="mb-4 flex items-center gap-1.5">
			<Skeleton shape="text" className="h-3 w-20" />
			<Skeleton shape="text" className="h-3 w-12" />
			<Skeleton shape="text" className="h-3 w-24" />
		</div>
	)
}

/** The eyebrow names the page, so it is shown; only the product's name is unknown. */
function HeaderSkeleton() {
	return (
		<header className="mb-8">
			<Eyebrow className="mb-2">{PRODUCT_PAGE_EYEBROW}</Eyebrow>
			<Skeleton className="h-10 w-72" />
		</header>
	)
}

/**
 * The Product page while its first read is in flight.
 *
 * Stands in the real layout with each card's own loading body, rather than a centred spinner
 * or a second set of placeholder shapes: the page arrives into the shape it already occupies,
 * and no card can end up with two different loading states for the same wait.
 *
 * Static text is shown rather than greyed over — the eyebrow, the card titles and the bar's
 * and rail's captions are the page's own words, so a grey bar there would hide what the page
 * already knows and then resize when the read lands. Counts, labels and tile counts stay
 * generic: a skeleton that guessed them would be stating data it does not have.
 *
 * One announcement for the whole page: the bodies below are given no label of their own, so a
 * reader who cannot see the pulse is told once rather than section by section.
 */
export function ProductPageSkeleton() {
	return (
		<div aria-busy="true">
			<span className="sr-only">Loading this product…</span>

			<ProductPageLayout
				breadcrumb={<BreadcrumbSkeleton />}
				header={<HeaderSkeleton />}
				hierarchy={<ResolvingForBarSkeleton />}
				rail={<ProductFilterRailSkeleton />}
				main={
					<>
						<SectionCard title={PRODUCT_NAMING_TITLE}>
							<ProductNamingBodySkeleton />
						</SectionCard>
						<SectionCard title={BRAND_KIT_TITLE}>
							<AssetListBodySkeleton />
						</SectionCard>
						<SectionCard title={TEXT_LIST_TITLE}>
							<TextListBodySkeleton />
						</SectionCard>
						<SectionCard title={CAMPAIGN_LIST_TITLE}>
							<CampaignListBodySkeleton />
						</SectionCard>
						<SectionCard title={DELIVERABLES_TITLE}>
							<DeliverablesBodySkeleton />
						</SectionCard>
						<SectionCard title={APPLICABLE_RULES_TITLE}>
							<ApplicableRulesBodySkeleton />
						</SectionCard>
					</>
				}
			/>
		</div>
	)
}
